import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { PatientRoomControllerApi, PatientRoomPropertyControllerApi } from '../../api/generated';
import type { PatientRoomPropertyResponse, PatientRoomResponse } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch } from '../../store/hooks';
import { useHasPermission } from '../../hooks/useHasPermission';
import { PERMISSIONS } from '../../constants/permissions';
import { NEW_ROOM_ID, useRoomListEditing } from '../../hooks/useRoomListEditing';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { useTableSort } from '../UI';
import { DeleteRoomModal } from './DeleteRoomModal';
import { getRoomColumns } from './roomColumns';
import { DEFAULT_ROOMS_SORT } from './roomsConstants';
import { RoomListView } from '../RoomListView/RoomListView';

export const RoomsSettings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const locationId = locationIdParam ?? null;
  const canWrite = useHasPermission(PERMISSIONS.PatientRoomsWrite);

  const [rooms, setRooms] = useState<PatientRoomResponse[]>([]);
  const [allRoomProperties, setAllRoomProperties] = useState<PatientRoomPropertyResponse[]>([]);
  const {
    searchQuery,
    setSearchQuery,
    filteredRooms,
    editingRoomId,
    effectiveEditingRoomId,
    editingName,
    setEditingName,
    openPickerRoomId,
    roomToDelete,
    setRoomToDelete,
    startEditing,
    startCreating,
    stopEditing,
    openPicker,
    closePicker
  } = useRoomListEditing(rooms);

  const { sort, handleSortChange } = useTableSort(DEFAULT_ROOMS_SORT);

  const refreshRooms = useCallback(
    async (locId: string) => {
      try {
        const config = createApiConfiguration();
        const roomApi = new PatientRoomControllerApi(config);
        const response = await roomApi.listPatientRooms({ locationId: locId });
        setRooms(response.data);
      } catch {
        dispatch(showError({ message: t('fetch-error') }));
      }
    },
    [dispatch, t]
  );

  useEffect(() => {
    if (!locationId) return;
    const init = async () => {
      try {
        const config = createApiConfiguration();
        const [roomsRes, propertiesRes] = await Promise.all([
          new PatientRoomControllerApi(config).listPatientRooms({ locationId }),
          new PatientRoomPropertyControllerApi(config).listPatientRoomProperties()
        ]);
        setRooms(roomsRes.data);
        setAllRoomProperties(propertiesRes.data);
      } catch {
        dispatch(showError({ message: t('fetch-error') }));
      }
    };
    void init();
  }, [locationId, dispatch, t]);

  const startEditName = useCallback(
    (roomId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      startEditing(roomId, room?.description ?? '');
    },
    [rooms, startEditing]
  );

  // saveName, addProperty and removeProperty are the same PUT with a different
  // body; only what changes about the room differs.
  const updateRoom = useCallback(
    async (roomId: string, description: string, patientRoomPropertyIds: string[]) => {
      if (!locationId) return;
      try {
        const api = new PatientRoomControllerApi(createApiConfiguration());
        await api.updatePatientRoom({
          locationId,
          patientRoomId: roomId,
          updatePatientRoomRequest: { description, patientRoomPropertyIds }
        });
        await refreshRooms(locationId);
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, refreshRooms, dispatch, t]
  );

  const saveName = useCallback(
    async (roomId: string, name: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      stopEditing();
      await updateRoom(
        roomId,
        name,
        room.patientRoomProperties.map((rp) => rp.id)
      );
    },
    [rooms, stopEditing, updateRoom]
  );

  const createRoom = useCallback(
    async (name: string) => {
      if (!locationId) return;
      try {
        const config = createApiConfiguration();
        const api = new PatientRoomControllerApi(config);
        await api.createPatientRoom({
          locationId,
          createPatientRoomRequest: { description: name, patientRoomPropertyIds: [] }
        });
        stopEditing();
        await refreshRooms(locationId);
      } catch {
        dispatch(showError({ message: t('update-error') }));
      }
    },
    [locationId, refreshRooms, stopEditing, dispatch, t]
  );

  const addProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(roomId, room.description, [
        ...room.patientRoomProperties.map((rp) => rp.id),
        propertyId
      ]);
    },
    [rooms, updateRoom]
  );

  const removeProperty = useCallback(
    async (roomId: string, propertyId: string) => {
      const room = rooms.find((r) => r.id === roomId);
      if (!room) return;
      await updateRoom(
        roomId,
        room.description,
        room.patientRoomProperties.map((rp) => rp.id).filter((id) => id !== propertyId)
      );
    },
    [rooms, updateRoom]
  );

  const handleDeleted = useCallback(() => {
    setRoomToDelete(null);
    if (locationId) {
      void refreshRooms(locationId);
    }
  }, [locationId, refreshRooms, setRoomToDelete]);

  const columns = useMemo(
    () =>
      getRoomColumns(t, {
        editingRoomId: effectiveEditingRoomId,
        editingName,
        openPickerRoomId,
        allRoomProperties,
        onStartEditName: startEditName,
        onNameChange: setEditingName,
        onSaveName: () => {
          if (!effectiveEditingRoomId || effectiveEditingRoomId === NEW_ROOM_ID) return;
          const trimmed = editingName.trim();
          if (trimmed.length === 0) return;
          void saveName(effectiveEditingRoomId, trimmed);
        },
        onCancelEdit: stopEditing,
        onOpenPicker: openPicker,
        onClosePicker: closePicker,
        onAddProperty: (roomId, propertyId) => {
          void addProperty(roomId, propertyId);
        },
        onRemoveProperty: (roomId, propertyId) => {
          void removeProperty(roomId, propertyId);
        },
        onDelete: (room) => setRoomToDelete(room),
        canWrite
      }),
    [
      t,
      effectiveEditingRoomId,
      editingName,
      openPickerRoomId,
      allRoomProperties,
      startEditName,
      saveName,
      stopEditing,
      setEditingName,
      setRoomToDelete,
      openPicker,
      closePicker,
      addProperty,
      removeProperty,
      canWrite
    ]
  );

  return (
    <RoomListView
      title={t('settings-rooms-header')}
      searchLabel={t('settings-search-room')}
      searchPlaceholder={t('rooms-search-placeholder')}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      columns={columns}
      rooms={filteredRooms}
      getRowKey={(room) => room.id}
      sort={sort}
      onSortChange={handleSortChange}
      canWrite={canWrite}
      addLabel={t('add-room')}
      isAddingRoom={editingRoomId === NEW_ROOM_ID}
      onStartAdd={startCreating}
      onSaveNewRoom={(name) => void createRoom(name)}
      onCancelAdd={stopEditing}
    >
      {canWrite && roomToDelete && locationId && (
        <DeleteRoomModal
          room={roomToDelete}
          locationId={locationId}
          onClose={() => setRoomToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </RoomListView>
  );
};
